import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Slide,
  Stack,
  TextField
} from "@mui/material";
import AppBar from "@mui/material/AppBar";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import {TransitionProps} from "@mui/material/transitions";
import Typography from "@mui/material/Typography";
import React, {ChangeEvent, useEffect, useState} from "react";
import {Intent, IntentRequest, MoneyColor, Pocket} from "../logic/types";
import LoadingButton from '@mui/material/Button';
import {toLocalDateString} from "../logic/date";
import MoneyKindView from "./MoneyKindView";
import {useLifePlatformApiClient} from "../hooks/useLifePlatformApiClient";
import {changePocket} from "../logic/helpers";

export type Props = {
  onAddedIntent?: () => void
  onChangedIntent?: () => void
  onDeletedIntent?: () => void
  show: boolean
  onClose: () => void
  intent?: Intent
  moneyColor?: MoneyColor
  selectedMonth: Date
  pockets: Array<Pocket>
  initialFromPocketId?: string
  initialToPocketId?: string
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="left" ref={ref} {...props} children={props.children}/>;
});

export default function IntentDialog(props: Props) {
  const closeDialog = () => {
    props.onClose()
  }

  const {apiAddIntent, apiChangeIntent, apiDeleteIntent, apiIgnoreIntent} = useLifePlatformApiClient();

  interface IntentDialogState {
    amount: string;
    description: string;
    paymentDate: string;
    periodInMonths: number;
    finishDate: string;
    fromPocket: string;
    toPocket: string;
    lastFromPocket: string;
    lastToPocket: string;
  }

  // State management using a single state object
  const [state, setState] = useState<IntentDialogState>({
    amount: "",
    description: "",
    paymentDate: getDatePickerString(props.selectedMonth),
    periodInMonths: 0,
    finishDate: "",
    fromPocket: "",
    toPocket: "",
    lastFromPocket: "",
    lastToPocket: "",
  });

  // Destructure state for easier access
  const {
    amount,
    description,
    paymentDate,
    periodInMonths,
    finishDate,
    fromPocket,
    toPocket,
    lastFromPocket,
    lastToPocket
  } = state;

  useEffect(() => {
    if (!props.show) return

    if (props.intent) {
      setState({
        amount: props.intent.amount.toString(),
        description: props.intent.description,
        paymentDate: getDatePickerString(new Date(props.intent.paymentDate)),
        periodInMonths: props.intent.periodInMonths,
        finishDate: props.intent.finishDate ? props.intent.finishDate.substring(0, 10) : "",
        fromPocket: props.intent.fromPocketId ?? props.initialFromPocketId ?? '',
        lastFromPocket: props.intent.fromPocketId ?? props.initialFromPocketId ?? '',
        toPocket: props.intent.toPocketId ?? props.initialToPocketId ?? '',
        lastToPocket: props.intent.toPocketId ?? props.initialToPocketId ?? ''
      })
    } else {
      setState({
        amount: "",
        description: "",
        paymentDate: getDatePickerString(props.selectedMonth),
        periodInMonths: 0,
        finishDate: "",
        fromPocket: props.initialFromPocketId ?? '',
        lastFromPocket: props.initialFromPocketId ?? '',
        toPocket: props.initialToPocketId ?? '',
        lastToPocket: props.initialToPocketId ?? ''
      })
    }
  }, [props.show, props.intent, props.moneyColor, props.selectedMonth, props.initialFromPocketId, props.initialToPocketId])

  function getDatePickerString(date: Date): string {
    let month = ('00' + (date.getMonth() + 1)).substr(-2)
    let day = ('00' + date.getDate()).substr(-2)
    return `${date.getFullYear()}-${month}-${day}`
  }

  async function onAddOrChange() {
    if (props.intent)
      await onChangeIntent(props.intent.id)
    else
      await onAddIntent()
  }

  // Update handlers to use setState
  function handleChangeAmount(event: ChangeEvent<HTMLInputElement>) {
    setState(prev => ({...prev, amount: event.target.value}));
  }

  function handleChangePeriod(event: ChangeEvent<HTMLInputElement>) {
    setState(prev => ({...prev, periodInMonths: parseInt(event.target.value) || 0}));
  }

  function handleChangeDescription(event: ChangeEvent<HTMLInputElement>) {
    setState(prev => ({...prev, description: event.target.value}));
  }

  function handleChangeFinishDate(event: ChangeEvent<HTMLInputElement>) {
    setState(prev => ({...prev, finishDate: event.target.value}));
  }

  function handleChangePaymentDate(event: ChangeEvent<HTMLInputElement>) {
    setState(prev => ({...prev, paymentDate: event.target.value}));
  }

  // Update setDescription for quick buttons
  function setDescription(desc: string) {
    setState(prev => ({...prev, description: desc}));
  }

  async function onAddIntent() {
    console.log('onAddIntent')

    console.log(`paymentDate ${paymentDate}. periodInMonth ${periodInMonths}`)
    const request: IntentRequest = {
      amount: parseInt(amount),
      paymentDate: toLocalDateString(new Date(paymentDate)),
      finishDate: finishDate ? toLocalDateString(new Date(finishDate)) : undefined,
      periodInMonths: periodInMonths,
      description: description,
      fromPocketId: fromPocket !== "" ? fromPocket : undefined,
      toPocketId: toPocket !== "" ? toPocket : undefined,
    }

    const added = await apiAddIntent(request)
    if (added) {
      if (props.onAddedIntent)
        props.onAddedIntent()
      closeDialog()
    }
  }

  async function onChangeIntent(intentID: string) {
    console.log('STEP onChangeIntent')

    const request: IntentRequest = {
      amount: parseInt(amount),
      paymentDate: toLocalDateString(new Date(paymentDate)),
      finishDate: finishDate ? toLocalDateString(new Date(finishDate)) : undefined,
      periodInMonths: periodInMonths,
      description: description,
      fromPocketId: fromPocket !== "" ? fromPocket : undefined,
      toPocketId: toPocket !== "" ? toPocket : undefined,
    }

    const changed = await apiChangeIntent(intentID, request)
    if (changed) {
      if (props.onChangedIntent)
        props.onChangedIntent()
      closeDialog()
    }
  }

  async function onDeleteIntent() {
    console.log('onDeleteIntent')

    const intentID = props.intent?.id
    if (!intentID) {
      console.log('onDeleteIntent: intentID is undefined')
      return
    }

    const deleted = await apiDeleteIntent(intentID, props.selectedMonth)
    if (deleted) {
      if (props.onDeletedIntent)
        props.onDeletedIntent()
      closeDialog()
    }
  }

  async function onIgnoreIntent() {
    console.log('onIgnoreIntent')

    const intentID = props.intent?.id
    if (!intentID) {
      console.log('onIgnoreIntent: intentID is undefined')
      return
    }

    const deleted = await apiIgnoreIntent(intentID, props.selectedMonth)
    if (deleted) {
      if (props.onDeletedIntent)
        props.onDeletedIntent()
      closeDialog()
    }
  }

  function validateForm(): boolean {
    return parseInt(amount) > 0;
  }

  const handleChangePockets = (from: string, to: string) => {
    const res = changePocket({...state, fromPocket: from, toPocket: to})
    setState(prev => ({
      ...prev,
      ...res,
    }));
  }

  const handleChangeFromPocket = (event: SelectChangeEvent) => {
    handleChangePockets(event.target.value as string, toPocket);
  };

  const handleChangeToPocket = (event: SelectChangeEvent) => {
    handleChangePockets(fromPocket, event.target.value as string);
  };

  function mapPocketToMenuItem(pocket: Pocket) {
    return (
      <MenuItem key={pocket.id} value={pocket.id}>{pocket.title}</MenuItem>
    )
  }

  return (
    <Dialog
      fullScreen
      open={props.show}
      onClose={closeDialog}
      TransitionComponent={Transition}
    >
      <AppBar
        style={{backgroundColor: "#6750A4"}}
        sx={{position: 'relative'}}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={closeDialog}
            aria-label="close"
          >
            <CloseIcon/>
          </IconButton>

          <Typography sx={{ml: 2, flex: 1}} variant="h6" component="div">
            запланирована трата
          </Typography>

          <LoadingButton color="inherit"
                         onClick={onAddOrChange}
                         disabled={!validateForm()}>
            {props.intent ? 'Изменить' : 'Добавить'}
          </LoadingButton>
        </Toolbar>
      </AppBar>

      <DialogContent>
        <Stack component="form" spacing={2}>
          <Stack direction="row" justifyContent="space-between">
            <TextField
              autoFocus
              label="Сумма"
              type="number"
              onChange={handleChangeAmount}
              value={amount}
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Дата начала"
              type="date"
              onChange={handleChangePaymentDate}
              value={paymentDate}
            />

            <TextField
              label="Дата окончания"
              InputLabelProps={{shrink: true}}
              sx={{minWidth: '120px'}}
              type="date"
              onChange={handleChangeFinishDate}
              value={finishDate}
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Переодичность"
              type="number"
              onChange={handleChangePeriod}
              value={periodInMonths}
            />
          </Stack>

          <TextField
            label="Описание"
            type="text"
            onChange={handleChangeDescription}
            value={description}
          />

          <Box>
            <Button variant="text" onClick={() => setDescription("Аренда")}>
              Аренда
            </Button>

            <Button variant="text" onClick={() => setDescription("Сейф")}>
              Сейф
            </Button>

            <Button variant="text" onClick={() => setDescription("Развлечения")}>
              Развлечения
            </Button>

            <Button variant="text" onClick={() => setDescription("Обучение")}>
              Обучение
            </Button>

            <Button variant="text" onClick={() => setDescription("Коты")}>
              Коты
            </Button>
          </Box>

          <FormControl fullWidth>
            <InputLabel>Из кошелька</InputLabel>
            <Select
              value={fromPocket}
              label="Из кошелька"
              onChange={handleChangeFromPocket}
            >
              <MenuItem value={''}><i>Не выбран</i></MenuItem>
              {props.pockets.map(mapPocketToMenuItem)}
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>В кошелек</InputLabel>
            <Select
              value={toPocket}
              label="В кошелек"
              onChange={handleChangeToPocket}
            >
              <MenuItem value={''}><i>Не выбран</i></MenuItem>
              {props.pockets.map(mapPocketToMenuItem)}
            </Select>
          </FormControl>

          <MoneyKindView fromPocketId={fromPocket} toPocketId={toPocket}/>

        </Stack>
      </DialogContent>

      <DialogActions>
        {props.intent && (
          <>
            <Button
              variant="outlined"
              color="error"
              onClick={onDeleteIntent}
            >
              Удалить
            </Button>

            <Button
              variant="outlined"
              onClick={onIgnoreIntent}
            >
              Игнорировать
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
